class ProductListComponent extends HTMLElement {
  constructor() {
    super();
    this.productGids = [];
    this.products = [];
    this.isLoggedIn = false;
  }
  
  // Get configuration from attributes with fallbacks
  get shopDomain() {
    return this.getAttribute('shop-domain') || window.Shopify?.shop || '';
  }
  
  get storefrontToken() {
    return this.getAttribute('storefront-token') || '';
  }
  
  get loggedIn() {
    // Only use the explicit logged-in attribute
    const loggedInAttr = this.getAttribute('logged-in');
    return loggedInAttr === 'true';
  }
  
  connectedCallback() {
    // Set login status using only the attribute
    this.isLoggedIn = this.loggedIn;
    
    console.log('Login status detected:', this.isLoggedIn);
    
    // If user is not logged in, show login button
    if (!this.isLoggedIn) {
      this.renderLoginButton();
      return;
    }
    
    // Get product GIDs and load product data
    this.loadProductData();
  }
  
  renderLoginButton() {
    this.innerHTML = `
      <div class="tw-bg-white">
        <div class="tw-mx-auto tw-max-w-2xl tw-px-4 tw-py-16 sm:tw-px-6 sm:tw-py-24 lg:tw-max-w-7xl lg:tw-px-8">
          <div class="tw-text-center">
            <p class="tw-mb-4 tw-text-lg tw-text-gray-700">Please log in to view your product list</p>
            <a href="/account/login" class="tw-inline-flex tw-items-center tw-justify-center tw-px-5 tw-py-3 tw-border tw-border-transparent tw-text-base tw-font-medium tw-rounded-md tw-text-white tw-bg-indigo-600 hover:tw-bg-indigo-700">
              Log in
            </a>
          </div>
        </div>
      </div>
    `;
  }
  
  async loadProductData() {
    try {
      // Check if product data is explicitly provided via attribute
      const productDataAttr = this.getAttribute('product-data');
      
      if (productDataAttr) {
        // Handle both JSON string and array cases
        try {
          // First try parsing as JSON
          this.productGids = JSON.parse(productDataAttr);
        } catch (e) {
          // If parsing fails, check if it's already an array
          if (Array.isArray(productDataAttr)) {
            this.productGids = productDataAttr;
          } else {
            // If it's neither valid JSON nor an array, log error and render empty state
            console.error('Invalid product-data format:', e);
            this.renderEmptyState();
            return;
          }
        }
      } else {
        this.renderEmptyState();
        return;
      }
      
      // Validate shop domain and token
      if (!this.shopDomain || !this.storefrontToken) {
        console.error('Missing shop-domain or storefront-token attributes');
        this.renderError('Configuration error: Missing shop domain or storefront token');
        return;
      }
      
      // Once we have the GIDs, fetch product data
      if (this.productGids && this.productGids.length > 0) {
        await this.fetchProductData();
      } else {
        this.renderEmptyState();
      }
    } catch (error) {
      console.error('Error loading product data:', error);
      this.renderError();
    }
  }
  
  async fetchProductData() {
    try {
      if (!this.productGids || this.productGids.length === 0) {
        this.renderEmptyState();
        return;
      }
      
      // Fetch all products in a single query
      const products = await this.fetchProductsByGids(this.productGids);
      
      this.products = products;
      
      if (products.length > 0) {
        this.renderProducts();
      } else {
        this.renderEmptyState();
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
      this.renderError();
    }
  }
  
  async fetchProductsByGids(gids) {
    // Create a GraphQL query that fetches multiple products at once
    const query = `
      query GetProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            onlineStoreUrl
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            featuredImage {
              url
              altText
            }
          }
        }
      }
    `;
    
    try {
      console.log(`Fetching ${gids.length} products in batch`);
      
      const response = await fetch(`https://${this.shopDomain}/api/2025-07/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken
        },
        body: JSON.stringify({
          query,
          variables: {
            ids: gids
          }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        console.error('Query used:', query);
        console.error('Variables:', { ids: gids });
        return [];
      }
      
      if (!result.data || !result.data.nodes) {
        console.error('No product data returned');
        return [];
      }
      
      // Filter out any null responses (which can happen if a product is not found)
      return result.data.nodes.filter(node => node && node.id);
    } catch (error) {
      console.error(`Error in fetchProductsByGids:`, error);
      return [];
    }
  }
  
  renderProducts() {
    const html = `
      <div class="tw-bg-white">
        <div class="tw-mx-auto tw-max-w-2xl tw-px-4 tw-py-16 sm:tw-px-6 sm:tw-py-24 lg:tw-max-w-7xl lg:tw-px-8">
          <h2 class="tw-sr-only">Products</h2>

          <div class="tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 xl:tw-gap-x-8">
            ${this.products.map(product => `
              <a href="${product.onlineStoreUrl || '/products/' + product.handle}" class="tw-group" data-product-id="${product.id}">
                ${product.featuredImage 
                  ? `<img 
                      src="${product.featuredImage.url}" 
                      alt="${product.featuredImage.altText || ''}" 
                      class="tw-aspect-square tw-w-full tw-rounded-lg tw-bg-gray-200 tw-object-cover group-hover:tw-opacity-75 xl:tw-aspect-[7/8]" 
                      loading="lazy"
                    />`
                  : `<div class="tw-aspect-square tw-w-full tw-rounded-lg tw-bg-gray-200 tw-flex tw-items-center tw-justify-center xl:tw-aspect-[7/8]">
                      <span class="tw-text-gray-400">No image</span>
                    </div>`
                }
                <h3 class="tw-mt-4 tw-text-sm tw-text-gray-700">${product.title}</h3>
                <p class="tw-mt-1 tw-text-lg tw-font-medium tw-text-gray-900">
                  ${this.formatMoney(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                </p>
                ${product.compareAtPriceRange && product.compareAtPriceRange.minVariantPrice.amount > 0 && 
                  product.compareAtPriceRange.minVariantPrice.amount > product.priceRange.minVariantPrice.amount
                  ? `<p class="tw-mt-1 tw-text-sm tw-text-gray-500 tw-line-through">
                      ${this.formatMoney(product.compareAtPriceRange.minVariantPrice.amount, product.compareAtPriceRange.minVariantPrice.currencyCode)}
                    </p>`
                  : ''
                }
              </a>
            `).join('')}
          </div>
        </div>
      </div>
    `;
    
    this.innerHTML = html;
  }
  
  renderEmptyState() {
    this.innerHTML = `
      <div class="tw-bg-white">
        <div class="tw-mx-auto tw-max-w-2xl tw-px-4 tw-py-16 sm:tw-px-6 sm:tw-py-24 lg:tw-max-w-7xl lg:tw-px-8">
          <h2 class="tw-sr-only">Products</h2>
          <p class="tw-text-center tw-py-8 tw-text-gray-500">No products found</p>
        </div>
      </div>
    `;
  }
  
  renderError(message = 'Failed to load products') {
    this.innerHTML = `
      <div class="tw-bg-white">
        <div class="tw-mx-auto tw-max-w-2xl tw-px-4 tw-py-16 tw-sm:tw-px-6 tw-sm:tw-py-24 tw-lg:tw-max-w-7xl tw-lg:tw-px-8">
          <h2 class="tw-sr-only">Products</h2>
          <p class="tw-text-center tw-py-8 tw-text-red-500">${message}</p>
        </div>
      </div>
    `;
  }
  
  formatMoney(amount, currencyCode = 'USD') {
    // Format money based on the currency code
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }
  
  disconnectedCallback() {
    // Cleanup if needed
  }
}

// Register the custom element
customElements.define('product-list', ProductListComponent);